// SPDX-License-Identifier: AGPL-3.0-or-later

import {SettingsSection} from '@app/features/app/components/dialogs/shared/SettingsSection';
import {SettingsTabContainer, SettingsTabContent} from '@app/features/app/components/dialogs/shared/SettingsTabLayout';
import MobileLayout from '@app/features/ui/state/MobileLayout';
import {ActiveNowTabContent} from '@app/features/user/components/modals/tabs/appearance_tab/ActiveNowTab';
import {ChannelListTabContent} from '@app/features/user/components/modals/tabs/appearance_tab/ChannelListTab';
import {FavoritesTabContent} from '@app/features/user/components/modals/tabs/appearance_tab/FavoritesTab';
import {HdrTabContent, shouldShowHdrSettings} from '@app/features/user/components/modals/tabs/appearance_tab/HdrTab';
import {InterfaceTabContent} from '@app/features/user/components/modals/tabs/appearance_tab/InterfaceTab';
import {
	AppearanceTabPreview,
	MessagesTabContent,
} from '@app/features/user/components/modals/tabs/appearance_tab/MessagesTab';
import {
	AppZoomLevelTabContent,
	FontSizeTabContent,
	useAppZoomLevelDescription,
} from '@app/features/user/components/modals/tabs/appearance_tab/ScalingTab';
import {ThemeTabContent} from '@app/features/user/components/modals/tabs/appearance_tab/theme/ThemeTabContent';
import {shouldShowAppZoomLevel} from '@app/features/user/components/settings_utils/AppZoomLevelUtils';
import {useLingui} from '@lingui/react/macro';
import {observer} from 'mobx-react-lite';
import type React from 'react';
export const AppearanceTab: React.FC = observer(() => {
	const {t} = useLingui();
	const showZoomLevel = shouldShowAppZoomLevel();
	const showHdrSettings = shouldShowHdrSettings();
	const appZoomLevelDescription = useAppZoomLevelDescription();
	return (
		<SettingsTabContainer>
			{!MobileLayout.enabled && <AppearanceTabPreview />}
			<SettingsTabContent>
				<SettingsSection
					id="theme"
					title={t`Theme`}
					description={t`Choose a base appearance, manage Quick CSS, organize saved theme files, and tune theme tokens.`}
				>
					<ThemeTabContent />
				</SettingsSection>
				{showHdrSettings ? (
					<SettingsSection
						id="hdr"
						title={t`High Dynamic Range`}
						description={t`Control how HDR images are displayed on HDR-capable monitors.`}
					>
						<HdrTabContent />
					</SettingsSection>
				) : null}
				<SettingsSection
					id="chat-font-scaling"
					title={t`Chat Font Scaling`}
					description={t`Adjust the font size in the chat area.`}
				>
					<FontSizeTabContent />
				</SettingsSection>
				{showZoomLevel ? (
					<SettingsSection id="app-zoom-level" title={t`App Zoom Level`} description={appZoomLevelDescription}>
						<AppZoomLevelTabContent />
					</SettingsSection>
				) : null}
				<SettingsSection
					id="messages"
					title={t`Messages`}
					description={t`Choose how messages are displayed in chat channels.`}
				>
					<MessagesTabContent />
				</SettingsSection>
				<SettingsSection
					id="interface"
					title={t`Interface`}
					description={t`Customize interface elements and behaviors.`}
				>
					<InterfaceTabContent />
				</SettingsSection>
				<SettingsSection
					id="channel-list"
					title={t`Channel List`}
					description={t`Control unread indicator behavior for muted channels in channel lists.`}
				>
					<ChannelListTabContent />
				</SettingsSection>
				<SettingsSection
					id="active-now"
					title={t`Active Now`}
					description={t`Control how Active Now surfaces across the app.`}
				>
					<ActiveNowTabContent />
				</SettingsSection>
				<SettingsSection
					id="favorites"
					title={t`Favorites`}
					description={t`Control the visibility of favorites throughout the app.`}
				>
					<FavoritesTabContent />
				</SettingsSection>
			</SettingsTabContent>
		</SettingsTabContainer>
	);
});
